import ChatThread from "./ChatThread";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Calendar, CalendarDays, Star, Send, Clock, FileText, MessageSquare, BadgeCheck } from "lucide-react";
import { api, fileUrl, formatApiError, timeAgo } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarPicker } from "../components/ui/calendar";

const FUNNEL = [
  ["new", "Applied"], ["screening", "Screening"], ["shortlisted", "Shortlisted"],
  ["interview", "Interview"], ["hired", "Hired"], ["rejected", "Rejected"],
];
const SCORE_KEYS = [["skills", "Skills"], ["experience", "Experience"], ["communication", "Communication"], ["culture_fit", "Culture fit"], ["overall", "Overall"]];
const MSG_TEMPLATES = [
  ["", "Write from scratch"],
  ["progressed", "Progressed. Awaiting contact"],
];

function DateField({ value, onChange, testid, className = "" }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} aria-label={selected ? `Change date, currently ${selected.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : "Pick a date"}
          className={`h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white text-left inline-flex items-center gap-2 ${className}`}>
          <CalendarDays className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
          {selected ? selected.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Pick a date"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <CalendarPicker mode="single" selected={selected}
          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
          onSelect={(d) => onChange(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "")} />
      </PopoverContent>
    </Popover>
  );
}

export default function CandidatePanel({ applicationId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [cvViewer, setCvViewer] = useState(null);
  const [status, setStatus] = useState("new");
  const [statusNote, setStatusNote] = useState("");
  const [iv, setIv] = useState({ date: "", time: "", link: "", location: "", note: "" });
  const [scores, setScores] = useState({});
  const [scoreComment, setScoreComment] = useState("");
  const [noteText, setNoteText] = useState("");
  const [msg, setMsg] = useState({ subject: "", body: "", send_date: "", send_time: "", client_key: "" });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState("");

  const load = useCallback(() => {
    if (!applicationId) return;
    api.get(`/employer/applications/${applicationId}/detail`).then((r) => {
      const a = r.data.application;
      setData(r.data);
      setStatus(a.status === "reviewed" ? "screening" : a.status);
      setScores(a.scorecard || {});
      setScoreComment(a.scorecard?.comment || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, successMsg) => {
    setSaving(key);
    try {
      await fn();
      toast.success(successMsg);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };

  const applyTemplate = (val) => {
    if (val !== "progressed") return;
    const a = data?.application;
    setMsg((m) => ({
      ...m,
      subject: `Your application is progressing: ${a?.job_title} at ${a?.company_name}`,
      body: `Good news. Your application for ${a?.job_title} at ${a?.company_name} is progressing well.\n\nWe are not quite ready to confirm the next step, but you are very much still in the running and we will be in contact shortly with details.\n\nYou do not need to do anything right now. If your availability changes, just reply to this email.`,
    }));
  };

  const a = data?.application;

  return (
    <Dialog open={!!applicationId} onOpenChange={onClose}>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="candidate-panel">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">
            <span className="inline-flex items-center gap-2">
              {a ? a.candidate_name : "Candidate"}
              {a?.candidate_verified && <BadgeCheck className="h-5 w-5 text-blue-600" data-testid="cp-verified-badge" />}
            </span>
          </DialogTitle>
        </DialogHeader>
        {!a ? (
          <div className="py-10 space-y-3">{[...Array(3)].map((_, i) => <div key={`cp-skel-${i}`} className="h-16 bg-zinc-100 rounded-md animate-pulse" />)}</div>
        ) : (
          <div className="space-y-8 mt-2">
            {/* summary */}
            <div className="text-sm text-zinc-600 space-y-1" data-testid="cp-summary">
              <p>{a.candidate_email}{a.phone ? ` · ${a.phone}` : ""}</p>
              <p className="text-xs text-zinc-400">
                {a.job_title} at {a.company_name} · via {a.source}{a.device ? ` · ${a.device}` : ""} · applied {timeAgo(a.created_at)}
              </p>
              {a.cv_path && /\.(jpe?g|png|webp)$/i.test(a.cv_filename || a.cv_path) && (
                <button type="button" onClick={() => setCvViewer({ src: fileUrl(a.cv_path), alt: a.cv_filename || "CV photo" })} data-testid="cp-cv"
                  className="text-sm font-medium text-blue-700 hover:text-blue-900 inline-block">
                  View CV{a.cv_filename ? ` (${a.cv_filename})` : ""}
                </button>
              )}
              {a.cv_path && !/\.(jpe?g|png|webp)$/i.test(a.cv_filename || a.cv_path) && (
                <a href={fileUrl(a.cv_path)} target="_blank" rel="noopener noreferrer" data-testid="cp-cv"
                  className="text-sm font-medium text-blue-700 hover:text-blue-900 inline-block">
                  Download CV{a.cv_filename ? ` (${a.cv_filename})` : ""}
                </a>
              )}
              {a.cover_note && <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line border-l-2 border-zinc-200 pl-3 mt-2">{a.cover_note}</p>}
              {a.profile_snapshot && (a.profile_snapshot.headline || a.profile_snapshot.availability || a.profile_snapshot.salary_expectation) && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="cp-profile-snapshot">
                  {[
                    a.profile_snapshot.headline,
                    a.profile_snapshot.availability && `Availability: ${a.profile_snapshot.availability}`,
                    a.profile_snapshot.salary_expectation && `Salary: ${a.profile_snapshot.salary_expectation}`,
                  ].filter(Boolean).join(" · ")}
                </p>
              )}
              {Object.keys(a.answers || {}).length > 0 && (
                <div className="mt-3 space-y-2 border-t border-zinc-100 pt-3" data-testid="cp-answers">
                  {Object.entries(a.answers).map(([q, ans], i) => (
                    <div key={q} className="text-sm">
                      <p className="font-medium text-zinc-800">{q}</p>
                      <p className="text-zinc-600 whitespace-pre-line" data-testid={`cp-answer-${i}`}>{ans || "No answer"}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* status */}
            <section>
              <h3 className="text-sm font-bold text-zinc-950 flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Status</h3>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cp-status-select"
                  className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                  {FUNNEL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Note (optional, internal)"
                  data-testid="cp-status-note"
                  className="flex-1 min-w-[160px] h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <button onClick={() => run("status", () => api.patch(`/employer/applications/${applicationId}`, { status, note: statusNote }), "Status updated. The candidate has been emailed.")}
                  disabled={saving === "status"} data-testid="cp-status-save"
                  className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                  {saving === "status" ? "Saving…" : "Update"}
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5">Changing the status emails the candidate automatically (once per stage).</p>
              {a.status_history?.length > 0 && (
                <ol className="mt-3 space-y-1.5 border-l border-zinc-200 pl-4" data-testid="cp-history">
                  {[...a.status_history].reverse().map((h, i) => (
                    <li key={`${h.to}-${h.at || i}`} className="text-xs text-zinc-600">
                      <span className="font-semibold text-zinc-900">{FUNNEL.find(([v]) => v === h.to)?.[1] || h.to}</span>
                      {h.from ? ` (from ${FUNNEL.find(([v]) => v === h.from)?.[1] || h.from})` : ""}
                      {h.at ? ` · ${timeAgo(h.at)}` : ""}{h.note ? ` · ${h.note}` : ""}
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {/* interview */}
            <section>
              <h3 className="text-sm font-bold text-zinc-950 flex items-center gap-2 mb-3"><Calendar className="h-4 w-4" /> Interview</h3>
              {a.interview?.when_str && (
                <p className="text-xs bg-blue-50 text-blue-900 rounded-md px-3 py-2 mb-3" data-testid="cp-interview-current">
                  Scheduled: {a.interview.when_str}{a.interview.link ? ` · ${a.interview.link}` : ""}{a.interview.location ? ` · ${a.interview.location}` : ""}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <DateField value={iv.date} onChange={(v) => setIv({ ...iv, date: v })} testid="cp-interview-date" className="w-full" />
                <input type="time" value={iv.time} onChange={(e) => setIv({ ...iv, time: e.target.value })} data-testid="cp-interview-time"
                  className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <input value={iv.link} onChange={(e) => setIv({ ...iv, link: e.target.value })} placeholder="Meeting link (Zoom/Teams/Meet)"
                  data-testid="cp-interview-link" className="col-span-2 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <input value={iv.location} onChange={(e) => setIv({ ...iv, location: e.target.value })} placeholder="Or location (if in person)"
                  data-testid="cp-interview-location" className="col-span-2 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              </div>
              <button onClick={() => run("iv", () => api.post(`/employer/applications/${applicationId}/interview`, { ...iv, send: true }).then((r) => { setIv({ date: "", time: "", link: "", location: "", note: "" }); return r; }), "Interview saved and invitation emailed.")}
                disabled={saving === "iv"} data-testid="cp-interview-save"
                className="mt-2 h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {saving === "iv" ? "Saving…" : "Save and send invitation"}
              </button>
              <p className="text-[11px] text-zinc-400 mt-1.5">The candidate gets the invitation with date, time and link, plus an automatic reminder 24 hours before.</p>
            </section>

            {/* scorecard */}
            <section>
              <h3 className="text-sm font-bold text-zinc-950 flex items-center gap-2 mb-3"><Star className="h-4 w-4" /> Scorecard <span className="text-[10px] font-normal text-zinc-400">internal only</span></h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="cp-scorecard">
                {SCORE_KEYS.map(([k, label]) => (
                  <label key={k} className="text-xs text-zinc-600">
                    {label}
                    <select value={scores[k] || ""} onChange={(e) => setScores({ ...scores, [k]: e.target.value ? Number(e.target.value) : "" })}
                      data-testid={`cp-score-${k}`}
                      className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                      <option value="">-</option>
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <input value={scoreComment} onChange={(e) => setScoreComment(e.target.value)} placeholder="Scorecard comment (optional)"
                data-testid="cp-score-comment" className="mt-2 w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <button onClick={() => run("score", () => api.put(`/employer/applications/${applicationId}/scorecard`, { ...scores, comment: scoreComment }), "Scorecard saved.")}
                disabled={saving === "score"} data-testid="cp-score-save"
                className="mt-2 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50">
                Save scorecard
              </button>
            </section>

            {/* notes */}
            <section>
              <h3 className="text-sm font-bold text-zinc-950 flex items-center gap-2 mb-3"><FileText className="h-4 w-4" /> Recruiter notes <span className="text-[10px] font-normal text-zinc-400">internal only</span></h3>
              <div className="flex gap-2">
                <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note about this candidate"
                  data-testid="cp-note-input" className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <button onClick={() => { const t = noteText; setNoteText(""); run("note", () => api.post(`/employer/applications/${applicationId}/notes`, { text: t }), "Note added."); }}
                  disabled={saving === "note" || !noteText.trim()} data-testid="cp-note-add"
                  className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50">Add</button>
              </div>
              {a.notes?.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="cp-notes">
                  {[...a.notes].reverse().map((n, i) => (
                    <li key={`${n.by}-${n.at || i}`} className="text-xs text-zinc-600 bg-zinc-50 rounded-md px-3 py-2">{n.text} <span className="text-zinc-400">· {n.by} · {timeAgo(n.at)}</span></li>
                  ))}
                </ul>
              )}
            </section>

            {/* messages */}
            <section>
              <h3 className="text-sm font-bold text-zinc-950 flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4" /> Message the candidate</h3>
              <select onChange={(e) => applyTemplate(e.target.value)} defaultValue="" data-testid="cp-msg-template"
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white mb-2">
                {MSG_TEMPLATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} placeholder="Subject"
                data-testid="cp-msg-subject" className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm mb-2" />
              {preview ? (
                <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50/60 text-sm text-zinc-700 leading-relaxed whitespace-pre-line mb-2" data-testid="cp-msg-preview-pane">
                  <p className="font-semibold text-zinc-950 mb-2">{msg.subject || "(No subject)"}</p>
                  {msg.body || "(Empty message)"}
                </div>
              ) : (
                <textarea rows={5} value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })}
                  placeholder="Write your message. It is sent as a branded email with your contact details."
                  data-testid="cp-msg-body" className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm resize-y mb-2" />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setPreview(!preview)} data-testid="cp-msg-preview"
                  className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500">
                  {preview ? "Edit" : "Preview"}
                </button>
                <DateField value={msg.send_date} onChange={(v) => setMsg({ ...msg, send_date: v })} testid="cp-msg-date" />
                <input type="time" value={msg.send_time} onChange={(e) => setMsg({ ...msg, send_time: e.target.value })}
                  data-testid="cp-msg-time" title="Leave date and time empty to send now"
                  className="h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <button
                  onClick={() => {
                    if (msg.send_date && !msg.send_time) return toast.error("Pick a time for the scheduled message");
                    const sendAt = msg.send_date ? `${msg.send_date}T${msg.send_time}` : "";
                    const key = crypto.randomUUID();
                    run("msg", () => api.post(`/employer/applications/${applicationId}/message`, { subject: msg.subject, body: msg.body, send_at: sendAt, client_key: key }),
                      sendAt ? "Message scheduled." : "Message sent.");
                    setMsg({ subject: "", body: "", send_date: "", send_time: "", client_key: "" });
                  }}
                  disabled={saving === "msg" || !msg.body.trim()} data-testid="cp-msg-send"
                  className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5" /> {msg.send_date ? "Schedule" : "Send now"}
                </button>
              </div>
              {data.communications?.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="cp-comms">
                  {data.communications.map((c) => (
                    <li key={c.id} className="text-xs text-zinc-600 flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.failed ? "bg-red-50 text-red-700" : c.sent_at ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                        {c.failed ? "FAILED" : c.sent_at ? "SENT" : "SCHEDULED"}
                      </span>
                      <span className="font-medium text-zinc-800">{c.subject}</span>
                      <span className="text-zinc-400">{c.failed ? "delivery failed · resend from here" : c.sent_at ? timeAgo(c.sent_at) : `for ${new Date(c.scheduled_at).toLocaleString("en-GB")}`} · {c.sent_by === "system" ? "automatic" : c.sent_by}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* live chat */}
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Chat with candidate</p>
              <p className="text-[11px] text-zinc-400 mb-2">Instant messages, the candidate is emailed if unread after an hour.</p>
              <ChatThread applicationId={applicationId} viewerRole="employer" testid="cp-chat" />
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
